export namespace HttpClient {
  type StreamBody = ReadableStream | null;

  interface ResponseImplementation<B = unknown> {
    headers: Headers;
    status: number;
    statusText: string;
    body: B;
    streamBody: StreamBody;
  }

  export class Response<B = unknown> implements ResponseImplementation {
    headers: Headers;
    status: number;
    statusText: string;
    body: B;
    streamBody: StreamBody;

    constructor({
      headers,
      status,
      statusText,
      body,
      streamBody,
    }: {
      headers: Headers;
      status: number;
      statusText: string;
      body: B;
      streamBody: StreamBody;
    }) {
      this.headers = headers;
      this.status = status;
      this.statusText = statusText;
      this.body = body;
      this.streamBody = streamBody;
    }
  }

  export type Methods = 'get' | 'post' | 'put' | 'patch' | 'delete';
  export type SearchParamsArrayFormat = 'indexes' | 'comma';

  export interface DefaultOptions extends Pick<BaseOptions, 'headers'> {
    baseUrl?: string;
  }

  export interface BaseOptions {
    headers?: Record<string, string>;
    signal?: AbortSignal;
    cache?: RequestCache;
    timeout?: number;
  }

  type BodyOption<B> = B extends undefined ? {} : { body: B };
  type ParamsOption<P> = P extends undefined ? {} : { params: P };
  type SearchParamsOption<S> = S extends undefined
    ? {}
    : {
        searchParams: S;
        searchParamsArrayFormat?: SearchParamsArrayFormat;
      };

  export type Options<
    B extends any | undefined = undefined,
    P extends undefined | Params = undefined,
    S extends undefined | SearchParams = undefined
  > = DefaultOptions &
    BodyOption<B> &
    ParamsOption<P> &
    SearchParamsOption<S> &
    BaseOptions;

  export type Params = Record<string, string | number | boolean>;
  export type SearchParams = Record<string, any> | URLSearchParams;

  export type ErrorReporter =
    | null
    | ((
        error: unknown,
        context: {
          url: string;
          options?: RequestInit;
          defaultOptions?: DefaultOptions;
        }
      ) => void);

  export type RequestInterceptor = (
    config: Partial<Options<any, Params, SearchParams>>
  ) =>
    | Partial<Options<any, Params, SearchParams>>
    | Promise<Partial<Options<any, Params, SearchParams>>>;

  export type ResponseInterceptor<R = any> = (
    response: Response<R>
  ) => Response<R> | Promise<Response<R>>;

  export type Interceptors = {
    request?: RequestInterceptor;
    response?: ResponseInterceptor;
  };

  export interface Instance {
    get<
      R,
      P extends undefined | Params = undefined,
      S extends undefined | SearchParams = undefined
    >(
      url: string,
      options?: Options<undefined, P, S>
    ): Promise<Response<R>>;
    post<
      R,
      B extends any | undefined = undefined,
      P extends undefined | Params = undefined,
      S extends undefined | SearchParams = undefined
    >(
      url: string,
      options?: Options<B, P, S>
    ): Promise<Response<R>>;
    put<
      R,
      B extends any | undefined = undefined,
      P extends undefined | Params = undefined,
      S extends undefined | SearchParams = undefined
    >(
      url: string,
      options?: Options<B, P, S>
    ): Promise<Response<R>>;
    patch<
      R,
      B extends any | undefined = undefined,
      P extends undefined | Params = undefined,
      S extends undefined | SearchParams = undefined
    >(
      url: string,
      options?: Options<B, P, S>
    ): Promise<Response<R>>;
    delete<
      R,
      P extends undefined | Params = undefined,
      S extends undefined | SearchParams = undefined
    >(
      url: string,
      options?: Options<undefined, P, S>
    ): Promise<Response<R>>;
    interceptors: Interceptors;
    errorReporter: ErrorReporter;
  }
}
